import React, { useCallback, useContext } from "react";
import { Stack, useTheme } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import HeaderButton from "@/modules/lead-page/new/components/task-management/header-button";
import { LeadPageContext } from "@/modules/lead-page/context/lead-page.context";
import {
  Banknote,
  Building2,
  ClipboardList,
  House,
  Heart,
  Lightbulb,
  MessageSquare,
  Newspaper,
  Star,
  ListTodo,
  Milestone,
  type LucideProps,
} from "lucide-react";
import { Box } from "@mui/material";
import ArchivedOverlay from "@/modules/common/components/ui-kit/archived-overlay";

/** design m_Start-flow: the "New" pill beside Nawy Now — Indicator/orange #E65F00, white 10/16. */
const NewPill = () => (
  <Box
    component="span"
    sx={{
      px: "4px",
      borderRadius: "4px",
      backgroundColor: "#E65F00",
      color: "#FFFFFF",
      fontSize: 10,
      lineHeight: "16px",
      fontWeight: 400,
      whiteSpace: "nowrap",
    }}
  >
    New
  </Box>
);
import LastActivityCard from "@/modules/lead-page/new/components/activity/last-acticity-card";
import { ActivityProvider } from "@/modules/lead-page/new/context/activity.context";
import LastActivityDate from "../activity/last-activity-date";
import ChatsAndCallsLastActivity from "../chats-and-calls/chats-and-calls-last-activity";

interface MobileActionCardsProps {
  isLeadArchived: boolean;
  onNawyNowClick: () => void;
  onTasksClick: () => void;
  onNextActionsClick: () => void;
  onRecommendationsClick: () => void;
  onActivitiesClick: () => void;
  onSalesClick: () => void;
  onPropertiesClick: () => void;
  onInterestsClick: () => void;
  onDeveloperTracksClick: () => void;
  onRatingsClick: () => void;
  isRatingsOpen: boolean;
  isDeveloperTracksVisible: boolean;
  isChatsAndCallsVisible: boolean;
  onChatsAndCallsClick: () => void;
}

export interface ActionCard {
  title: string;
  onClick: () => void;
  icon: React.ComponentType<LucideProps>;
  show?: boolean; // Optional condition to show/hide the card
  body?: React.ReactNode;
  caption?: React.ReactNode;
  /** optional pill beside the title — only Nawy Now uses it today */
  titleBadge?: React.ReactNode;
}

const MobileActionCards: React.FC<MobileActionCardsProps> = ({
  isLeadArchived,
  onNawyNowClick,
  onTasksClick,
  onNextActionsClick,
  onRecommendationsClick,
  onActivitiesClick,
  onSalesClick,
  onPropertiesClick,
  onInterestsClick,
  onDeveloperTracksClick,
  onRatingsClick,
  isRatingsOpen,
  isDeveloperTracksVisible,
  isChatsAndCallsVisible,
  onChatsAndCallsClick,
}) => {
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const { lead } = useContext(LeadPageContext);
  // §NawyNowGate: drives `show` on the Nawy Now action card below.
  const isNawyNowLead =
    lead?.data?.lead?.leadType?.name?.toLowerCase() === "nawy now";

  const handleNavigateToTrack = useCallback((trackUId: string) => {
    searchParams.set("trackUId", trackUId);
    setSearchParams(searchParams, { replace: true });
    onDeveloperTracksClick();
  }, [searchParams, setSearchParams, onDeveloperTracksClick]);

  const actionCards: ActionCard[] = [
    {
      title: "Activity feed",
      caption: (
        <ActivityProvider>
          <LastActivityDate />
        </ActivityProvider>
      ),
      body: (
        <ActivityProvider>
          <LastActivityCard onNavigateToTrack={handleNavigateToTrack} />
        </ActivityProvider>
      ),
      onClick: onActivitiesClick,
      icon: Newspaper,
    },
    // design m_Start-flow: the Nawy Now card sits right after the activity feed
    {
      title: "Nawy Now",
      caption: "Loan process & application.",
      onClick: onNawyNowClick,
      // design (m_Start-flow, node I12107:58309): the row's glyph is `outline icon/house`,
      // not the hand-of-coins this was using
      icon: House,
      // design: an orange Indicator/orange pill sits beside the title on this row alone
      titleBadge: <NewPill />,
      show: isNawyNowLead,
    },
    {
      title: "Chats & calls",
      caption: <ChatsAndCallsLastActivity />,
      onClick: onChatsAndCallsClick,
      icon: MessageSquare,
      show: isChatsAndCallsVisible,
    },
    {
      title: "Tasks",
      caption: "Manage lead tasks.",
      onClick: onTasksClick,
      icon: ListTodo,
    },
    {
      title: "Next actions",
      caption: "Tasks & reminders.",
      onClick: onNextActionsClick,
      icon: ClipboardList,
    },
    {
      title: "Recommendations",
      caption: "Similar properties.",
      onClick: onRecommendationsClick,
      icon: Lightbulb,
    },

    {
      title: "Sales",
      caption: "Sales information.",
      onClick: onSalesClick,
      icon: Building2,
    },
    {
      title: "Properties",
      caption: "Property details.",
      onClick: onPropertiesClick,
      icon: Banknote,
    },
    {
      title: "Interests",
      caption: "Interests details.",
      onClick: onInterestsClick,
      icon: Heart,
    },
    ...(isDeveloperTracksVisible
      ? [
          {
            title: "Developer Tracks",
            caption: "Track progress details.",
            onClick: onDeveloperTracksClick,
            icon: Milestone,
          },
        ]
      : []),
    ...(isRatingsOpen
      ? [
          {
            title: "Reviews",
            caption: "Reviews details.",
            onClick: onRatingsClick,
            icon: Star,
          },
        ]
      : []),
  ];

  return (
    <Stack direction="column" gap={theme.nawy.spacing.sm}>
      {actionCards
        .filter((card) => card.show !== false) // Filter out cards where show is explicitly false
        .map((card) => {
          const IconComponent = card.icon;
          return (
            <ArchivedOverlay key={card.title} isArchived={isLeadArchived}>
              <HeaderButton
                title={card.title}
                caption={card.caption}
                onClick={card.onClick}
                body={card.body}
                titleBadge={card.titleBadge}
                icon={
                  <IconComponent
                    color={theme.nawy.colors.semantic.text.accent}
                  />
                }
              />
            </ArchivedOverlay>
          );
        })}
    </Stack>
  );
};

export default MobileActionCards;
